/** Interface text for the navigation and the season pages (table, stats, players), merged into i18n.ts. */

const en = {
  nav: { label: "Pages", match: "Live match", season: "Season" },
  season: {
    title: "Season {{season}}",
    subtitle: "Standings after matchday {{md}} · {{played}} matches played",
    tabs: { table: "Table", stats: "Stats", players: "Players" },
    standingsAfter: "Standings after",
    matchday: "Matchday {{md}}",
    beforeSeason: "Before the season",
    preseason: "The season hasn't started yet. Pick a later matchday.",
    prev: "Previous matchday",
    next: "Next matchday",
    liveTag: "live now",
    liveHidden: "Matchday {{md}} is being played now, so this shows the standings before it: no spoilers.",
    liveShown: "This includes results from matchday {{md}}, which is being played now.",
    showAll: "Show the full season",
    hideLive: "Hide them",
    building: "Simulating the season… about a minute and a half, the first time only",
    error: "Couldn't load the season: {{error}}",
    vs: "v",
    at: "at",
    form: { W: "W", D: "D", L: "L" },
    unit: { km: "km", kmh: "km/h" },
    table: {
      pos: "Pos", position: "Position", club: "Club", p: "P", played: "Played", w: "W", won: "Won", d: "D",
      drawn: "Drawn", l: "L", lost: "Lost", gf: "GF", goalsFor: "Goals for", ga: "GA", goalsAgainst: "Goals against",
      gd: "GD", goalDifference: "Goal difference", pts: "Pts", points: "Points", form: "Form", next: "Next",
      md: "MD", matchdayLong: "Matchday", up: "Up {{n}}", down: "Down {{n}}", same: "No change",
      seePlayers: "See {{club}}'s players",
      zones: { ucl: "Champions League", uel: "Europa League", uecl: "Conference League", rel: "Relegation" },
    },
    stats: {
      players: "Player leaders",
      clubs: "Club leaders",
      seeAll: "All",
      cat: { g: "Goals", a: "Assists", xg: "Expected goals (xG)", kp: "Chances created", sot: "Shots on target",
             cs: "Clean sheets", sav: "Saves", tkw: "Tackles won", int: "Interceptions", km: "Distance covered",
             top: "Top speed" },
      club: { gf: "Goals scored", ga: "Fewest conceded", clean_sheets: "Clean sheets", xg: "Expected goals (xG)",
              possession: "Possession", pass_accuracy: "Pass accuracy" },
    },
    players: {
      search: "Search players",
      allClubs: "All clubs",
      position: "Position",
      all: "All",
      pos: { GK: "Goalkeepers", DEF: "Defenders", MID: "Midfielders", FWD: "Forwards" },
      per90: "Per 90 minutes",
      per90Note: "players with {{min}}+ minutes",
      count_one: "{{count}} player",
      count_other: "{{count}} players",
      none: "No players match.",
      showMore: "Show {{n}} more",
      col: { player: "Player", apps: "Apps", min: "Min", g: "G", a: "A", xg: "xG", sh: "Sh", sot: "SoT", kp: "CC",
             pass: "Pass", tkw: "Tkl", int: "Int", sav: "Sv", cs: "CS" },
      colTitle: { apps: "Appearances", min: "Minutes", g: "Goals", a: "Assists", xg: "Expected goals", sh: "Shots",
                  sot: "Shots on target", kp: "Chances created", pass: "Pass accuracy", tkw: "Tackles won",
                  int: "Interceptions", sav: "Saves", cs: "Clean sheets" },
    },
    player: {
      season: "Season so far (to matchday {{md}})",
      apps: "Appearances",
      starts: "{{n}} starts",
      minutes: "Minutes",
      per90: "per 90",
      vsXg: "vs xG",
      recoveries: "Ball recoveries",
      cards: "Yellow / red cards",
      physical: "Physical",
      distance: "Distance per 90",
      sprints: "Sprints per 90",
      topSpeed: "Top speed",
      matches: "Match by match",
      opponent: "Opponent",
      result: "Result",
      noMatches: "Hasn't played yet.",
      subNote: "* came off the bench",
      close: "Close",
    },
    position: { GK: "Goalkeeper", RB: "Right-back", CB: "Centre-back", LB: "Left-back", DM: "Defensive midfielder",
                CM: "Central midfielder", AM: "Attacking midfielder", RW: "Right winger", LW: "Left winger",
                ST: "Striker" },
  },
};

type SeasonDict = typeof en;

const es: SeasonDict = {
  nav: { label: "Páginas", match: "Partido en directo", season: "Temporada" },
  season: {
    title: "Temporada {{season}}",
    subtitle: "Clasificación tras la jornada {{md}} · {{played}} partidos jugados",
    tabs: { table: "Clasificación", stats: "Estadísticas", players: "Jugadores" },
    standingsAfter: "Clasificación tras",
    matchday: "Jornada {{md}}",
    beforeSeason: "Antes de la temporada",
    preseason: "La temporada aún no ha empezado. Elige una jornada posterior.",
    prev: "Jornada anterior",
    next: "Jornada siguiente",
    liveTag: "en directo",
    liveHidden: "La jornada {{md}} se está jugando ahora, así que se muestra la clasificación previa: sin spoilers.",
    liveShown: "Incluye resultados de la jornada {{md}}, que se está jugando ahora.",
    showAll: "Ver toda la temporada",
    hideLive: "Ocultarlos",
    building: "Simulando la temporada… alrededor de un minuto y medio, solo la primera vez",
    error: "No se pudo cargar la temporada: {{error}}",
    vs: "vs",
    at: "en",
    form: { W: "G", D: "E", L: "P" },
    unit: { km: "km", kmh: "km/h" },
    table: {
      pos: "Pos", position: "Posición", club: "Club", p: "PJ", played: "Jugados", w: "G", won: "Ganados", d: "E",
      drawn: "Empatados", l: "P", lost: "Perdidos", gf: "GF", goalsFor: "Goles a favor", ga: "GC",
      goalsAgainst: "Goles en contra", gd: "DG", goalDifference: "Diferencia de goles", pts: "Pts", points: "Puntos",
      form: "Forma", next: "Próximo", md: "J", matchdayLong: "Jornada", up: "Sube {{n}}", down: "Baja {{n}}",
      same: "Sin cambios", seePlayers: "Ver jugadores de {{club}}",
      zones: { ucl: "Champions League", uel: "Europa League", uecl: "Conference League", rel: "Descenso" },
    },
    stats: {
      players: "Líderes individuales",
      clubs: "Líderes por club",
      seeAll: "Todos",
      cat: { g: "Goles", a: "Asistencias", xg: "Goles esperados (xG)", kp: "Ocasiones creadas", sot: "Tiros a puerta",
             cs: "Porterías a cero", sav: "Paradas", tkw: "Entradas ganadas", int: "Intercepciones",
             km: "Distancia recorrida", top: "Velocidad máxima" },
      club: { gf: "Goles marcados", ga: "Menos goles recibidos", clean_sheets: "Porterías a cero",
              xg: "Goles esperados (xG)", possession: "Posesión", pass_accuracy: "Precisión de pase" },
    },
    players: {
      search: "Buscar jugadores",
      allClubs: "Todos los clubes",
      position: "Posición",
      all: "Todos",
      pos: { GK: "Porteros", DEF: "Defensas", MID: "Centrocampistas", FWD: "Delanteros" },
      per90: "Por 90 minutos",
      per90Note: "jugadores con {{min}}+ minutos",
      count_one: "{{count}} jugador",
      count_other: "{{count}} jugadores",
      none: "Ningún jugador coincide.",
      showMore: "Ver {{n}} más",
      col: { player: "Jugador", apps: "PJ", min: "Min", g: "G", a: "A", xg: "xG", sh: "Tir", sot: "TaP", kp: "OC",
             pass: "Pase", tkw: "Ent", int: "Int", sav: "Par", cs: "P0" },
      colTitle: { apps: "Partidos jugados", min: "Minutos", g: "Goles", a: "Asistencias", xg: "Goles esperados",
                  sh: "Tiros", sot: "Tiros a puerta", kp: "Ocasiones creadas", pass: "Precisión de pase",
                  tkw: "Entradas ganadas", int: "Intercepciones", sav: "Paradas", cs: "Porterías a cero" },
    },
    player: {
      season: "Temporada hasta ahora (hasta la jornada {{md}})",
      apps: "Partidos",
      starts: "{{n}} de titular",
      minutes: "Minutos",
      per90: "por 90",
      vsXg: "vs xG",
      recoveries: "Recuperaciones",
      cards: "Amarillas / rojas",
      physical: "Físico",
      distance: "Distancia por 90",
      sprints: "Sprints por 90",
      topSpeed: "Velocidad máxima",
      matches: "Partido a partido",
      opponent: "Rival",
      result: "Resultado",
      noMatches: "Aún no ha jugado.",
      subNote: "* entró desde el banquillo",
      close: "Cerrar",
    },
    position: { GK: "Portero", RB: "Lateral derecho", CB: "Central", LB: "Lateral izquierdo",
                DM: "Mediocentro defensivo", CM: "Centrocampista", AM: "Mediapunta", RW: "Extremo derecho",
                LW: "Extremo izquierdo", ST: "Delantero centro" },
  },
};

const fr: SeasonDict = {
  nav: { label: "Pages", match: "Match en direct", season: "Saison" },
  season: {
    title: "Saison {{season}}",
    subtitle: "Classement après la journée {{md}} · {{played}} matchs joués",
    tabs: { table: "Classement", stats: "Statistiques", players: "Joueurs" },
    standingsAfter: "Classement après",
    matchday: "Journée {{md}}",
    beforeSeason: "Avant la saison",
    preseason: "La saison n'a pas encore commencé. Choisissez une journée plus tardive.",
    prev: "Journée précédente",
    next: "Journée suivante",
    liveTag: "en direct",
    liveHidden: "La journée {{md}} est en cours : voici le classement d'avant, sans rien dévoiler.",
    liveShown: "Inclut les résultats de la journée {{md}}, en cours en ce moment.",
    showAll: "Voir toute la saison",
    hideLive: "Les masquer",
    building: "Simulation de la saison… environ une minute et demie, la première fois seulement",
    error: "Impossible de charger la saison : {{error}}",
    vs: "c.",
    at: "à",
    form: { W: "V", D: "N", L: "D" },
    unit: { km: "km", kmh: "km/h" },
    table: {
      pos: "Pos", position: "Position", club: "Club", p: "J", played: "Joués", w: "V", won: "Victoires", d: "N",
      drawn: "Nuls", l: "D", lost: "Défaites", gf: "BP", goalsFor: "Buts pour", ga: "BC", goalsAgainst: "Buts contre",
      gd: "Diff", goalDifference: "Différence de buts", pts: "Pts", points: "Points", form: "Forme", next: "Prochain",
      md: "J", matchdayLong: "Journée", up: "Gagne {{n}} place(s)", down: "Perd {{n}} place(s)", same: "Inchangé",
      seePlayers: "Voir les joueurs de {{club}}",
      zones: { ucl: "Ligue des champions", uel: "Ligue Europa", uecl: "Ligue Conférence", rel: "Relégation" },
    },
    stats: {
      players: "Meilleurs joueurs",
      clubs: "Meilleurs clubs",
      seeAll: "Tous",
      cat: { g: "Buts", a: "Passes décisives", xg: "Buts attendus (xG)", kp: "Occasions créées", sot: "Tirs cadrés",
             cs: "Clean sheets", sav: "Arrêts", tkw: "Tacles gagnés", int: "Interceptions", km: "Distance parcourue",
             top: "Vitesse maximale" },
      club: { gf: "Buts marqués", ga: "Moins de buts encaissés", clean_sheets: "Clean sheets",
              xg: "Buts attendus (xG)", possession: "Possession", pass_accuracy: "Précision des passes" },
    },
    players: {
      search: "Rechercher un joueur",
      allClubs: "Tous les clubs",
      position: "Poste",
      all: "Tous",
      pos: { GK: "Gardiens", DEF: "Défenseurs", MID: "Milieux", FWD: "Attaquants" },
      per90: "Par 90 minutes",
      per90Note: "joueurs avec {{min}}+ minutes",
      count_one: "{{count}} joueur",
      count_other: "{{count}} joueurs",
      none: "Aucun joueur ne correspond.",
      showMore: "Afficher {{n}} de plus",
      col: { player: "Joueur", apps: "MJ", min: "Min", g: "B", a: "PD", xg: "xG", sh: "Tirs", sot: "Cad", kp: "OC",
             pass: "Passes", tkw: "Tac", int: "Int", sav: "Arr", cs: "CS" },
      colTitle: { apps: "Matchs joués", min: "Minutes", g: "Buts", a: "Passes décisives", xg: "Buts attendus",
                  sh: "Tirs", sot: "Tirs cadrés", kp: "Occasions créées", pass: "Précision des passes",
                  tkw: "Tacles gagnés", int: "Interceptions", sav: "Arrêts", cs: "Clean sheets" },
    },
    player: {
      season: "La saison jusqu'ici (jusqu'à la journée {{md}})",
      apps: "Matchs",
      starts: "{{n}} titularisations",
      minutes: "Minutes",
      per90: "par 90",
      vsXg: "vs xG",
      recoveries: "Récupérations",
      cards: "Cartons jaunes / rouges",
      physical: "Physique",
      distance: "Distance par 90",
      sprints: "Sprints par 90",
      topSpeed: "Vitesse maximale",
      matches: "Match par match",
      opponent: "Adversaire",
      result: "Résultat",
      noMatches: "N'a pas encore joué.",
      subNote: "* entré en jeu",
      close: "Fermer",
    },
    position: { GK: "Gardien", RB: "Latéral droit", CB: "Défenseur central", LB: "Latéral gauche",
                DM: "Milieu défensif", CM: "Milieu central", AM: "Milieu offensif", RW: "Ailier droit",
                LW: "Ailier gauche", ST: "Avant-centre" },
  },
};

const pt: SeasonDict = {
  nav: { label: "Páginas", match: "Jogo ao vivo", season: "Temporada" },
  season: {
    title: "Temporada {{season}}",
    subtitle: "Classificação após a jornada {{md}} · {{played}} jogos disputados",
    tabs: { table: "Classificação", stats: "Estatísticas", players: "Jogadores" },
    standingsAfter: "Classificação após",
    matchday: "Jornada {{md}}",
    beforeSeason: "Antes da temporada",
    preseason: "A temporada ainda não começou. Escolha uma jornada posterior.",
    prev: "Jornada anterior",
    next: "Jornada seguinte",
    liveTag: "ao vivo",
    liveHidden: "A jornada {{md}} está a ser disputada agora, por isso mostramos a classificação anterior: sem spoilers.",
    liveShown: "Inclui resultados da jornada {{md}}, que está a ser disputada agora.",
    showAll: "Ver a temporada completa",
    hideLive: "Ocultá-los",
    building: "A simular a temporada… cerca de um minuto e meio, só da primeira vez",
    error: "Não foi possível carregar a temporada: {{error}}",
    vs: "vs",
    at: "em",
    form: { W: "V", D: "E", L: "D" },
    unit: { km: "km", kmh: "km/h" },
    table: {
      pos: "Pos", position: "Posição", club: "Clube", p: "J", played: "Jogos", w: "V", won: "Vitórias", d: "E",
      drawn: "Empates", l: "D", lost: "Derrotas", gf: "GM", goalsFor: "Golos marcados", ga: "GS",
      goalsAgainst: "Golos sofridos", gd: "DG", goalDifference: "Diferença de golos", pts: "Pts", points: "Pontos",
      form: "Forma", next: "Próximo", md: "J", matchdayLong: "Jornada", up: "Sobe {{n}}", down: "Desce {{n}}",
      same: "Sem alteração", seePlayers: "Ver jogadores do {{club}}",
      zones: { ucl: "Liga dos Campeões", uel: "Liga Europa", uecl: "Liga Conferência", rel: "Despromoção" },
    },
    stats: {
      players: "Líderes individuais",
      clubs: "Líderes por clube",
      seeAll: "Todos",
      cat: { g: "Golos", a: "Assistências", xg: "Golos esperados (xG)", kp: "Ocasiões criadas", sot: "Remates à baliza",
             cs: "Jogos sem sofrer", sav: "Defesas", tkw: "Desarmes ganhos", int: "Interceções",
             km: "Distância percorrida", top: "Velocidade máxima" },
      club: { gf: "Golos marcados", ga: "Menos golos sofridos", clean_sheets: "Jogos sem sofrer",
              xg: "Golos esperados (xG)", possession: "Posse de bola", pass_accuracy: "Precisão de passe" },
    },
    players: {
      search: "Procurar jogadores",
      allClubs: "Todos os clubes",
      position: "Posição",
      all: "Todos",
      pos: { GK: "Guarda-redes", DEF: "Defesas", MID: "Médios", FWD: "Avançados" },
      per90: "Por 90 minutos",
      per90Note: "jogadores com {{min}}+ minutos",
      count_one: "{{count}} jogador",
      count_other: "{{count}} jogadores",
      none: "Nenhum jogador corresponde.",
      showMore: "Mostrar mais {{n}}",
      col: { player: "Jogador", apps: "J", min: "Min", g: "G", a: "A", xg: "xG", sh: "Rem", sot: "RàB", kp: "OC",
             pass: "Passe", tkw: "Des", int: "Int", sav: "Def", cs: "JSS" },
      colTitle: { apps: "Jogos", min: "Minutos", g: "Golos", a: "Assistências", xg: "Golos esperados",
                  sh: "Remates", sot: "Remates à baliza", kp: "Ocasiões criadas", pass: "Precisão de passe",
                  tkw: "Desarmes ganhos", int: "Interceções", sav: "Defesas", cs: "Jogos sem sofrer" },
    },
    player: {
      season: "A temporada até agora (até à jornada {{md}})",
      apps: "Jogos",
      starts: "{{n}} como titular",
      minutes: "Minutos",
      per90: "por 90",
      vsXg: "vs xG",
      recoveries: "Recuperações",
      cards: "Amarelos / vermelhos",
      physical: "Físico",
      distance: "Distância por 90",
      sprints: "Sprints por 90",
      topSpeed: "Velocidade máxima",
      matches: "Jogo a jogo",
      opponent: "Adversário",
      result: "Resultado",
      noMatches: "Ainda não jogou.",
      subNote: "* entrou do banco",
      close: "Fechar",
    },
    position: { GK: "Guarda-redes", RB: "Lateral direito", CB: "Defesa central", LB: "Lateral esquerdo",
                DM: "Médio defensivo", CM: "Médio centro", AM: "Médio ofensivo", RW: "Extremo direito",
                LW: "Extremo esquerdo", ST: "Ponta de lança" },
  },
};

const de: SeasonDict = {
  nav: { label: "Seiten", match: "Live-Spiel", season: "Saison" },
  season: {
    title: "Saison {{season}}",
    subtitle: "Tabelle nach dem {{md}}. Spieltag · {{played}} Spiele gespielt",
    tabs: { table: "Tabelle", stats: "Statistiken", players: "Spieler" },
    standingsAfter: "Tabelle nach",
    matchday: "{{md}}. Spieltag",
    beforeSeason: "Vor der Saison",
    preseason: "Die Saison hat noch nicht begonnen. Wählen Sie einen späteren Spieltag.",
    prev: "Vorheriger Spieltag",
    next: "Nächster Spieltag",
    liveTag: "läuft",
    liveHidden: "Der {{md}}. Spieltag läuft gerade, daher sehen Sie die Tabelle davor: keine Spoiler.",
    liveShown: "Enthält Ergebnisse des {{md}}. Spieltags, der gerade läuft.",
    showAll: "Ganze Saison zeigen",
    hideLive: "Ausblenden",
    building: "Die Saison wird simuliert… etwa anderthalb Minuten, nur beim ersten Mal",
    error: "Die Saison konnte nicht geladen werden: {{error}}",
    vs: "gg.",
    at: "bei",
    form: { W: "S", D: "U", L: "N" },
    unit: { km: "km", kmh: "km/h" },
    table: {
      pos: "Pl.", position: "Platz", club: "Verein", p: "Sp", played: "Spiele", w: "S", won: "Siege", d: "U",
      drawn: "Unentschieden", l: "N", lost: "Niederlagen", gf: "T", goalsFor: "Tore", ga: "GT", goalsAgainst: "Gegentore",
      gd: "Diff", goalDifference: "Tordifferenz", pts: "Pkt", points: "Punkte", form: "Form", next: "Nächstes",
      md: "ST", matchdayLong: "Spieltag", up: "{{n}} Plätze hoch", down: "{{n}} Plätze runter", same: "Unverändert",
      seePlayers: "Spieler von {{club}} ansehen",
      zones: { ucl: "Champions League", uel: "Europa League", uecl: "Conference League", rel: "Abstieg" },
    },
    stats: {
      players: "Spieler-Bestenliste",
      clubs: "Vereins-Bestenliste",
      seeAll: "Alle",
      cat: { g: "Tore", a: "Vorlagen", xg: "Erwartete Tore (xG)", kp: "Kreierte Chancen", sot: "Schüsse aufs Tor",
             cs: "Zu-null-Spiele", sav: "Paraden", tkw: "Gewonnene Zweikämpfe", int: "Abgefangene Bälle",
             km: "Laufdistanz", top: "Höchstgeschwindigkeit" },
      club: { gf: "Erzielte Tore", ga: "Wenigste Gegentore", clean_sheets: "Zu-null-Spiele",
              xg: "Erwartete Tore (xG)", possession: "Ballbesitz", pass_accuracy: "Passquote" },
    },
    players: {
      search: "Spieler suchen",
      allClubs: "Alle Vereine",
      position: "Position",
      all: "Alle",
      pos: { GK: "Torhüter", DEF: "Abwehr", MID: "Mittelfeld", FWD: "Sturm" },
      per90: "Pro 90 Minuten",
      per90Note: "Spieler mit {{min}}+ Minuten",
      count_one: "{{count}} Spieler",
      count_other: "{{count}} Spieler",
      none: "Keine passenden Spieler.",
      showMore: "{{n}} weitere zeigen",
      col: { player: "Spieler", apps: "Sp", min: "Min", g: "T", a: "V", xg: "xG", sh: "Sch", sot: "aT", kp: "KC",
             pass: "Pass", tkw: "ZK", int: "Abf", sav: "Par", cs: "WS" },
      colTitle: { apps: "Einsätze", min: "Minuten", g: "Tore", a: "Vorlagen", xg: "Erwartete Tore", sh: "Schüsse",
                  sot: "Schüsse aufs Tor", kp: "Kreierte Chancen", pass: "Passquote", tkw: "Gewonnene Zweikämpfe",
                  int: "Abgefangene Bälle", sav: "Paraden", cs: "Zu-null-Spiele" },
    },
    player: {
      season: "Saison bisher (bis zum {{md}}. Spieltag)",
      apps: "Einsätze",
      starts: "{{n}} von Beginn",
      minutes: "Minuten",
      per90: "pro 90",
      vsXg: "ggü. xG",
      recoveries: "Ballgewinne",
      cards: "Gelbe / Rote Karten",
      physical: "Athletik",
      distance: "Laufdistanz pro 90",
      sprints: "Sprints pro 90",
      topSpeed: "Höchstgeschwindigkeit",
      matches: "Spiel für Spiel",
      opponent: "Gegner",
      result: "Ergebnis",
      noMatches: "Noch nicht eingesetzt.",
      subNote: "* eingewechselt",
      close: "Schließen",
    },
    position: { GK: "Torhüter", RB: "Rechter Verteidiger", CB: "Innenverteidiger", LB: "Linker Verteidiger",
                DM: "Defensives Mittelfeld", CM: "Zentrales Mittelfeld", AM: "Offensives Mittelfeld",
                RW: "Rechtsaußen", LW: "Linksaußen", ST: "Mittelstürmer" },
  },
};

export const SEASON_TEXT: Record<"en" | "es" | "fr" | "pt" | "de", SeasonDict> = { en, es, fr, pt, de };
